type PreviewTableRow = {
  year: string
  month: string
  description: string
  classObject: Record<string, boolean>
  empty: boolean
}

type HistoryItem = {
  year?: string
  month?: string
  description?: string
}

type PreviewTableResult = {
  rows: PreviewTableRow[]
  error: string
}

function createTableRow(
  year?: string,
  month?: string,
  desc?: string,
  classObject: Record<string, boolean> = {}
): PreviewTableRow {
  const yearStr = year || ''
  const monthStr = month || ''
  const description = desc || ''
  return {
    year: yearStr,
    month: monthStr,
    description,
    classObject,
    empty: (yearStr + monthStr + description).length === 0
  }
}

function toHistoryItems(raw: unknown): HistoryItem[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as HistoryItem[]
  if (typeof raw === 'object') {
    return Object.values(raw as Record<string, HistoryItem>)
  }
  return []
}

function padToMaxRows(
  rows: PreviewTableRow[],
  maxRows: number
): PreviewTableRow[] {
  const emptyRow = createTableRow('', '', '', { empty: true })
  const result = rows.slice()
  while (result.length <= maxRows) {
    result.push(emptyRow)
  }
  return result
}

type EduJobFormatData = {
  educationalHistories?: unknown
  jobHistories?: unknown
  notDisplayIjyo?: boolean
}

type LicenseFormatData = {
  licenseHistories?: unknown
  notDisplayIjyoLicense?: boolean
}

export function buildEduJobHistoryTable4New(
  formatData: EduJobFormatData | undefined | null,
  maxEduJobRows: number
): PreviewTableResult {
  const eduJobHistoryRows: PreviewTableRow[] = []
  let rowsCount = 0
  const educationalHistories = toHistoryItems(formatData?.educationalHistories)
  if (educationalHistories.length) {
    for (const item of educationalHistories) {
      const row = createTableRow(item.year, item.month, item.description)
      if (!row.empty) {
        eduJobHistoryRows.push(row)
        rowsCount++
      }
    }
  }

  if (rowsCount && !formatData?.notDisplayIjyo) {
    eduJobHistoryRows.push(
      createTableRow('', '', '以上', { 'end-of-content': true })
    )
  }

  let error = ''
  if (eduJobHistoryRows.length > maxEduJobRows) {
    const overRowsNum = String(eduJobHistoryRows.length - maxEduJobRows)
    error = `学歴の行数が ${overRowsNum} 行オーバーしています。義務教育課程の学歴を省略するか、別の履歴書を選んでください。`
  }

  return {
    rows: padToMaxRows(eduJobHistoryRows, maxEduJobRows),
    error
  }
}

export function buildEduJobHistoryTable(
  formatData: EduJobFormatData | undefined | null,
  maxEduJobRows: number
): PreviewTableResult {
  const eduJobHistoryRows: PreviewTableRow[] = []
  const emptyRow = createTableRow('', '', '', { empty: true })
  let rowsCount = 0
  const educationalHistories = toHistoryItems(formatData?.educationalHistories)
  if (educationalHistories.length) {
    eduJobHistoryRows.push(
      createTableRow('', '', '学歴', {
        education: true
      })
    )
    for (const item of educationalHistories) {
      const row = createTableRow(item.year, item.month, item.description)
      if (!row.empty) {
        eduJobHistoryRows.push(row)
        rowsCount++
      }
    }
    eduJobHistoryRows.push(emptyRow)
  }

  const jobHistories = toHistoryItems(formatData?.jobHistories)
  if (jobHistories.length) {
    eduJobHistoryRows.push(
      createTableRow('', '', '職歴', {
        experience: true
      })
    )
    let jobHistoryCount = 0
    for (const item of jobHistories) {
      const row = createTableRow(item.year, item.month, item.description)
      if (!row.empty) {
        jobHistoryCount++
        rowsCount++
        eduJobHistoryRows.push(row)
      }
    }
    if (jobHistoryCount === 0) {
      eduJobHistoryRows.push(
        createTableRow('', '', 'なし', {
          none: true
        })
      )
    }
  }

  if (rowsCount && !formatData?.notDisplayIjyo) {
    eduJobHistoryRows.push(
      createTableRow('', '', '以上', { 'end-of-content': true })
    )
  }

  let error = ''
  if (eduJobHistoryRows.length > maxEduJobRows) {
    const overRowsNum = String(eduJobHistoryRows.length - maxEduJobRows)
    error = `学歴・職歴の行数が ${overRowsNum} 行オーバーしています。義務教育課程の学歴を省略するか、別の履歴書を選んでください。`
  }

  return {
    rows: padToMaxRows(eduJobHistoryRows, maxEduJobRows),
    error
  }
}

export function buildLicenseHistory4NewTable(
  formatData: LicenseFormatData | undefined | null,
  maxLicense4NewRows: number
): PreviewTableResult {
  const licenseHistoryRows: PreviewTableRow[] = []
  const licenseHistories = toHistoryItems(formatData?.licenseHistories)
  if (licenseHistories.length === 0) {
    licenseHistoryRows.push(
      createTableRow('', '', 'なし', {
        none: true
      })
    )
  } else {
    for (const item of licenseHistories) {
      const row = createTableRow(item.year, item.month, item.description)
      if (!row.empty) {
        licenseHistoryRows.push(row)
      }
    }
  }

  let error = ''
  if (licenseHistoryRows.length > maxLicense4NewRows) {
    const overRowsNum = String(licenseHistoryRows.length - maxLicense4NewRows)
    error = `免許・資格の行数が ${overRowsNum} 行オーバーしています。`
  }

  return {
    rows: padToMaxRows(licenseHistoryRows, maxLicense4NewRows),
    error
  }
}

export function buildLicenseHistoryTable(
  formatData: LicenseFormatData | undefined | null,
  maxLicenseRows: number
): PreviewTableResult {
  const licenseHistoryRows: PreviewTableRow[] = []
  let rowsCount = 0
  const licenseHistories = toHistoryItems(formatData?.licenseHistories)
  if (licenseHistories.length === 0) {
    licenseHistoryRows.push(
      createTableRow('', '', 'なし', {
        none: true
      })
    )
  } else {
    for (const item of licenseHistories) {
      const row = createTableRow(item.year, item.month, item.description)
      if (!row.empty) {
        licenseHistoryRows.push(row)
        rowsCount++
      }
    }
  }

  let error = ''
  if (licenseHistoryRows.length > maxLicenseRows) {
    const overRowsNum = String(licenseHistoryRows.length - maxLicenseRows)
    error = `免許・資格の行数が ${overRowsNum} 行オーバーしています。`
  }

  if (rowsCount && !formatData?.notDisplayIjyoLicense) {
    licenseHistoryRows.push(
      createTableRow('', '', '以上', { 'end-of-content': true })
    )
  }

  return {
    rows: padToMaxRows(licenseHistoryRows, maxLicenseRows),
    error
  }
}
